import classNames from "classnames";
import { withDefault } from "collection-utils";
import AppIcon from "common/app-icon";
import { ButtonVNext } from "designer/ui/smart/button-vnext/button-vnext";
import { ButtonVariants, ButtonType } from "designer/ui/smart/button-vnext/button-vnext-lib";
import * as React from "react";
import styled, { css } from "styled-components";
import BaseModalV2 from "../base-modal/base-modal";

interface WindowedModalV2Props {
    readonly canClose: boolean;
    readonly onClose: () => void;

    readonly hideClosingButton?: boolean;
    readonly className?: string;
    readonly padding?: number;
    readonly title?: React.ReactNode;
    readonly titleAccessory?: React.ReactNode;
    readonly footer?: React.ReactNode;
    readonly floatingFooter?: React.ReactNode;
    readonly footerDivider?: boolean;
    readonly footerTopMargin?: number;
    readonly bigHeaderSize?: boolean;
    readonly barsPadding?: number;
}

const WindowedModalV2Style = styled.div<{
    padding: number;
    footerTopMargin: number;
    barsPadding: number;
    footerDivider: boolean;
    bigHeaderSize: boolean;
}>`
    border-radius: 12px;
    background-color: ${p => p.theme.bgColorLight};
    box-shadow: ${p => p.theme.shadow4};
    display: flex;
    flex-direction: column;
    color: ${p => p.theme.fgColorDark};
    overflow: hidden;
    z-index: 100;

    /* TEST: Need to verify this with existing modals */
    .close-button {
        height: 40px;
        width: 40px;
        margin: 16px 20px;
        position: absolute;
        top: 0px;
        right: 0px;
        padding: 8px;
        cursor: pointer;
        z-index: 1000;
        color: ${p => p.theme.n700};
        transition: color ${p => p.theme.transitionBase};

        &:hover {
            color: ${p => p.theme.fgColorDark};
        }
    }
    .title-bar {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 72px;
        padding: ${p => p.barsPadding ?? 0}px ${p => p.barsPadding}px 26px;
        .spacer {
            flex-grow: 0;
            flex-shrink: 10000000;
        }
        .title-accessory {
            &.pad-right {
                margin-right: 40px;
            }
        }
        .title-container {
            color: ${p => p.theme.fgColorDarkest};
            flex-grow: 1;
            font-size: ${p => (p.bigHeaderSize ? 26 : 18)}px;
            font-weight: 800;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }
    .content-area {
        padding: 0 ${p => p.padding}px;
        flex-grow: 1;
    }
    .footer {
        margin: ${p => p.footerTopMargin}px ${p => p.barsPadding}px 0px;
        padding: 26px 0px ${p => p.barsPadding ?? 26}px;
        ${p =>
            p.footerDivider &&
            css`
                border-top: 1px solid ${p.theme.borderPale};
            `}
    }

    .floatingFooter {
        margin: 26px 32px;
    }

    .transparent & {
        background-color: unset;
    }
`;

const ExtraWindowedModalV2Style = styled.div`
    border-radius: 8px;
    background-color: ${p => p.theme.bgColorLight};
    display: flex;
    flex-direction: column;
    color: ${p => p.theme.fgColorDark};
    overflow: hidden;
    z-index: 100;
    margin-top: 20px;

    .floatingFooter {
        margin: 26px 32px 32px;
    }
`;

export interface WindowedModalButton {
    title: string;
    variant: ButtonVariants;
    type: ButtonType;
    fullSize?: boolean;
    onClick: () => void;
    disabled?: boolean;
    href?: string;
}

interface Props {
    readonly className?: string;
    readonly align: "left" | "right" | "split";
    readonly buttons: readonly WindowedModalButton[];
}

const ButtonBarStyle = styled.div`
    display: flex;
    button {
        height: 36px;
    }
    > :not(:last-child) {
        margin-right: 12px;
    }
    &.right {
        justify-content: flex-end;
    }
    &.split {
        justify-content: space-between;
    }
`;

export const WindowedModalButtonBar: React.FunctionComponent<Props> = p => {
    return (
        <ButtonBarStyle className={classNames(p.className, p.align)}>
            {p.buttons.map(b => (
                <ButtonVNext
                    onClick={b.onClick}
                    size={"md"}
                    key={`${b.title}-${Math.random()}`}
                    variant={b.variant}
                    buttonType={b.type}
                    isFullWidth={b.fullSize}
                    disabled={b.disabled}
                    label={b.title}
                    href={b.href}
                />
            ))}
        </ButtonBarStyle>
    );
};

const WindowedModalV2: React.FunctionComponent<WindowedModalV2Props> = p => {
    const {
        hideClosingButton,
        canClose,
        onClose,
        title,
        titleAccessory,
        footer,
        floatingFooter,
        padding,
        className,
        footerDivider,
        footerTopMargin,
        bigHeaderSize,
        barsPadding,
    } = p;

    const hideTitle = title === undefined && titleAccessory === undefined;
    const paddingOrDefault = withDefault(padding, 32);
    const footerDividerOrDefault = withDefault(footerDivider, true);
    const footerTopMarginOrDefault = withDefault(footerTopMargin, 26);
    const bigHeaderSizeOrDefault = withDefault(bigHeaderSize, false);
    const barsPaddingOrDefault = withDefault(barsPadding, 32);

    const titleAccessoryClass = canClose ? "title-accessory pad-right" : "title-accessory";

    return (
        <BaseModalV2 className={className} canClickOff={canClose} onClickOff={onClose}>
            <WindowedModalV2Style
                padding={paddingOrDefault}
                footerDivider={footerDividerOrDefault}
                footerTopMargin={footerTopMarginOrDefault}
                bigHeaderSize={bigHeaderSizeOrDefault}
                barsPadding={barsPaddingOrDefault}>
                {!hideTitle && (
                    <div className="title-bar">
                        <div className="title-container">{title}</div>
                        <div className={titleAccessoryClass}>{titleAccessory}</div>
                        <div className="spacer" />
                    </div>
                )}
                {canClose && !hideClosingButton && (
                    <button className="close-button" onClick={onClose}>
                        <AppIcon icon="close" size={24} />
                    </button>
                )}
                <div className="content-area">{p.children}</div>
                {footer && <div className="footer">{footer}</div>}
            </WindowedModalV2Style>
            {floatingFooter && (
                <ExtraWindowedModalV2Style>
                    <div className="floatingFooter">{floatingFooter}</div>
                </ExtraWindowedModalV2Style>
            )}
        </BaseModalV2>
    );
};

export default WindowedModalV2;
